import React, { useRef } from 'react';
import useLang from '../../i18n/useLang';
import { SectionHeading } from '../site/PageParts';
import { Reveal, useCountUp, useInView, useSequence } from '../site/motion';
import { CheckIcon } from './icons';

// An illustrative test run: the kind of automated checks every interface gets before it touches the line.
const TESTS = [
  ['opcua', 'line3/counter.good → yield'],
  ['kepware', 'quality=bad → hold posting'],
  ['bartender', 'GS1-128 label · order 1000452'],
  ['sap', 'confirmation op 0020 · posted once'],
  ['network', 'plant server · no outbound traffic'],
];
const PASSED = 48;

function Status({ state }) {
  return (
    <span className="flex h-5 items-center justify-center">
      {state === 'pass' && <CheckIcon className="h-4 w-4 text-grovv-700" />}
      {state === 'run' && <span className="run-spin block h-3.5 w-3.5 rounded-full border-2 border-fog-200 border-t-grovv-600" />}
      {state === 'wait' && <span className="block h-1.5 w-1.5 rounded-full bg-fog-300" />}
    </span>
  );
}

function TestRun() {
  const { t } = useLang();
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, threshold: 0.4, rootMargin: '0px' });
  // Two steps per test: running, then passed.
  const total = TESTS.length * 2;
  const [step] = useSequence(total, { interval: 520, running: inView });
  const done = step >= total;
  const passedRows = Math.floor(step / 2);
  const count = useCountUp(PASSED, { start: done, duration: 900 });

  return (
    <figure ref={ref} className="overflow-hidden rounded-[1.75rem] bg-white shadow-float ring-1 ring-fog-200">
      <div aria-hidden="true" dir="ltr" className="text-left">
        <div className="flex items-center justify-between gap-3 border-b border-fog-200 px-5 py-3.5 font-mono text-[12.5px]">
          <span className="text-ink">test run · line 3</span>
          <span className={done ? 'text-grovv-800' : 'text-ink-500'}>{done ? 'done' : 'running'}</span>
        </div>
        <ul className="px-5 py-3 font-mono text-[12.5px]">
          {TESTS.map(([tag, text], i) => {
            const state = step >= 2 * i + 2 ? 'pass' : step === 2 * i + 1 ? 'run' : 'wait';
            return (
              <li key={tag} className="grid grid-cols-[1.25rem_5.25rem_minmax(0,1fr)] items-start gap-x-2 py-2">
                <Status state={state} />
                <span className="font-medium leading-5 text-ink">{tag}</span>
                <span className={`leading-5 [overflow-wrap:anywhere] ${state === 'wait' ? 'text-ink-500/60' : 'text-ink-600'}`}>{text}</span>
              </li>
            );
          })}
        </ul>
        <div className="border-t border-fog-200 px-5 py-4">
          <div className="h-1.5 overflow-hidden rounded-full bg-fog-100">
            <div className="run-bar h-full w-full rounded-full bg-grovv" style={{ transform: `scaleX(${passedRows / TESTS.length})` }} />
          </div>
          <div className={`mt-3 font-mono text-[12.5px] text-grovv-800 ${done ? '' : 'invisible'}`}>
            {count} passed · 0 failed
          </div>
        </div>
      </div>
      <figcaption className="border-t border-fog-200 bg-fog-50 px-5 py-3 text-[13px] text-ink-500">{t('home.control.panelCaption')}</figcaption>
    </figure>
  );
}

export default function FullControl() {
  const { t } = useLang();
  const items = t('home.control.items', { returnObjects: true });

  return (
    <section id="how" className="wrap scroll-mt-24 py-24 sm:py-32">
      <div className="grid grid-cols-1 gap-14 lg:grid-cols-[1.1fr_1fr] lg:gap-20">
        <div>
          <Reveal>
            <SectionHeading title={t('home.control.title')} lead={t('home.control.lead')} />
          </Reveal>
          <ol className="mt-12 border-t border-fog-200">
            {(Array.isArray(items) ? items : []).map((it, i) => (
              <Reveal as="li" key={it.title} delay={i * 70} variant="start" className="grid grid-cols-[2.75rem_minmax(0,1fr)] gap-x-2 border-b border-fog-200 py-6">
                <span className="pt-1 font-mono text-sm tabular-nums text-grovv-800">{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <h3 className="text-lg font-semibold text-ink">{it.title}</h3>
                  <p className="mt-1.5 leading-relaxed text-ink-600">{it.body}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
        <div className="lg:pt-28">
          <div className="lg:sticky lg:top-28">
            <TestRun />
          </div>
        </div>
      </div>
    </section>
  );
}
